import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Design System
import { Message } from 'jl-design-system/components/message/Message';
import { PrimaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { PromoCodeProps } from 'types/PromoCode.types';
import { AppDispatch } from 'types/RootState.types';

// Config
import applyPromoCodeState from './ApplyPromoCode.state';
import { ANIMATION_TYPES } from '../../constants/giftOptionsConstants';
import paymentTypeConstants from '../../constants/paymentTypeConstants';
import { applyPromoCode, removePromoCode } from '../../redux/actions/payment/promoCodeActions';

// Types
import { ApplyPromoCodeProps } from './ApplyPromoCode.types';

// Components
import Container from '../container';
import PaymentDeductionsList from '../payment-deductions/PaymentDeductionsList';
import PromoCodeForm from '../payment-deductions/promo-code-form';
import RewardsPromoWarning from '../rewards-promo-warning/RewardsPromoWarning';
import Rewards from '../rewards/Rewards';

// Styles
import styles from './apply-promo-code.scss';

const ApplyPromoCode = ({
  onDone = () => {},
}: ApplyPromoCodeProps) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    applyPromoCodeFailed,
    applyPromoCodeFailedCode,
    removePromoCodeLoading,
    outstandingBalance,
    promoCodeApplied,
    applyPromoCodeLoading,
    promoCodeError,
    rewardApplicable,
    rewardApplied,
    promoCodeEntry,
  } = useSelector(applyPromoCodeState);

  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [promoCodeLoadingCancelled, setPromoCodeLoadingCancelled] = useState<boolean>(false);
  const [promoCodeId, setPromoCodeId] = useState<string>('');

  const handleApplyPromoCode = (promoCode: { promoCode: string }) => dispatch(applyPromoCode(promoCode));
  const handleRemovePromoCode = (item: PromoCodeProps) => dispatch(removePromoCode(item));

  const handleUndoPromoCode = (item: PromoCodeProps) => {
    if (rewardApplied) {
      setShowWarningModal(true);
      setPromoCodeLoadingCancelled(false);
      if (item.id) {
        setPromoCodeId(item.id);
      }
      return;
    }

    dispatch(applyPromoCode({ promoCode: item.id, isUndo: true }));
  };

  const handleOnCancel = () => {
    setShowWarningModal(false);
    setPromoCodeLoadingCancelled(true);
  };

  const handleOnContinue = (promoCodeId: string) => {
    dispatch(applyPromoCode({ promoCode: promoCodeId }));
    setShowWarningModal(false);
  };

  const handleOnClose = () => {
    if (!promoCodeEntry || promoCodeEntry.promoCode === applyPromoCodeFailedCode) {
      onDone();
    } else {
      dispatch(applyPromoCode({ promoCode: promoCodeEntry.promoCode, isOnClose: true }));
    }
  };

  const determineAnimationType = (promoCodeApplied: PromoCodeProps[]) => {
    const activePromoCodes = promoCodeApplied?.filter(promo => !promo.isRemoved);
    if (activePromoCodes.length === 0 && promoCodeApplied.length === 0) {
      return ANIMATION_TYPES.WHOLE_SECTION;
    }
    return ANIMATION_TYPES.NEW_ITEM;

  };

  const bodyText = rewardApplicable ?
    'You can only use one promo code or reward per order' :
    'You can only use one promo code per order';

  return (
    <>
      <Container marginBottom="2">
        <Message
          body={bodyText}
          className={styles.message}
          type="information"
        />
      </Container>

      <div className={styles.rewards}>
        <Rewards />
      </div>

      <div className={styles.promoCodeForm}>
        <PromoCodeForm
          onSubmitForm={handleApplyPromoCode}
          promoCodeError={promoCodeError}
          rewardApplied={rewardApplied}
          showNewPromoCodeOption
        />
      </div>

      {
        promoCodeApplied &&
        <div className={styles.paymentDeductionsList}>
          <PaymentDeductionsList
            animation={determineAnimationType([promoCodeApplied])}
            applyGiftPaymentFailed={applyPromoCodeFailed}
            balance={outstandingBalance}
            isRemovable
            list={[promoCodeApplied] as PromoCodeProps[]}
            onRemove={handleRemovePromoCode}
            onUndo={handleUndoPromoCode}
            promoCodeLoadingCancelled={promoCodeLoadingCancelled}
            showUndo
            type={paymentTypeConstants.PROMO_CODE}
          />
        </div>
      }

      <PrimaryButton
        className={styles.doneButton}
        data-testid="apply-promo-code-done-button"
        disabled={applyPromoCodeLoading || removePromoCodeLoading}
        onClick={handleOnClose}
      >
        Done
      </PrimaryButton>

      {
        showWarningModal &&
          <RewardsPromoWarning
            direction="promo-to-reward"
            onCancel={handleOnCancel}
            onContinue={() => handleOnContinue(promoCodeId)}
          />
      }
    </>
  );
};

export default ApplyPromoCode;
